import { z } from "zod/mini";
import type { KeyValueStore } from "../platform.ts";
import { storageName } from "./names.ts";

/**
 * FR-015-032, FR-015-130, `spec.md` § Key Entities: one versioned wrapper with a Zod Mini schema
 * per key for view state, drafts, preferences, the last route per workspace, the sidebar
 * and group collapse state and the peek width. A value that fails its schema is migrated
 * when a migration exists and discarded otherwise, never thrown on.
 */
export { STORAGE_PREFIX, storageName } from "./names.ts";

export interface StoredEnvelope {
  readonly v: number;
  readonly data: unknown;
}

export interface StorageKey<T> {
  readonly name: string;
  readonly version: number;
  readonly schema: z.ZodMiniType<T>;
  /** Migrate an envelope from an older version; return null to discard it. */
  readonly migrate?: (from: number, data: unknown) => T | null;
}

export function defineKey<T>(key: StorageKey<T>): StorageKey<T> {
  return key;
}

export interface TypedStorage {
  get<T>(key: StorageKey<T>, scope?: string): T | null;
  set<T>(key: StorageKey<T>, value: T, scope?: string): void;
  remove<T>(key: StorageKey<T>, scope?: string): void;
  /** Every scope stored under a key, for a sweep such as clearing drafts. */
  scopesOf<T>(key: StorageKey<T>): string[];
}

export function createTypedStorage(store: KeyValueStore): TypedStorage {
  return {
    get(key, scope) {
      const raw = store.get(storageName(key.name, scope));
      if (raw === null) return null;
      let envelope: StoredEnvelope;
      try {
        envelope = JSON.parse(raw) as StoredEnvelope;
      } catch {
        store.remove(storageName(key.name, scope));
        return null;
      }
      if (typeof envelope !== "object" || typeof envelope.v !== "number") {
        store.remove(storageName(key.name, scope));
        return null;
      }
      let data = envelope.data;
      if (envelope.v !== key.version) {
        const migrated = key.migrate?.(envelope.v, data) ?? null;
        if (migrated === null) {
          store.remove(storageName(key.name, scope));
          return null;
        }
        data = migrated;
        this.set(key, migrated, scope);
      }
      const parsed = key.schema.safeParse(data);
      if (!parsed.success) {
        store.remove(storageName(key.name, scope));
        return null;
      }
      return parsed.data;
    },
    set(key, value, scope) {
      const envelope: StoredEnvelope = { v: key.version, data: value };
      store.set(storageName(key.name, scope), JSON.stringify(envelope));
    },
    remove(key, scope) {
      store.remove(storageName(key.name, scope));
    },
    scopesOf(key) {
      const prefix = `${storageName(key.name)}:`;
      return store
        .keys()
        .filter((k) => k.startsWith(prefix))
        .map((k) => k.slice(prefix.length));
    },
  };
}

/** The keys of `spec.md` § Key Entities. */
export const viewStateKey = defineKey({
  name: "view-state",
  version: 1,
  schema: z.object({
    layout: z.optional(z.string()),
    group_by: z.optional(z.string()),
    group_granularity: z.optional(z.enum(["day", "week", "month"])),
    group_buckets: z.optional(z.int().check(z.minimum(1), z.maximum(20))),
    /** The orderings in sequence, comma separated, `<target>[:desc]` each (002 § Ordering). */
    order_by: z.optional(z.string()),
    filters: z.optional(z.record(z.string(), z.array(z.string()))),
    display_properties: z.optional(z.array(z.string())),
    /** The Client column turned off in Display (2026-10-02): a list shows the column by itself while a task in it has a client, unless this is set. */
    client_hidden: z.optional(z.boolean()),
    collapsed_groups: z.optional(z.array(z.string())),
    collapsed_parents: z.optional(z.array(z.string())),
    subitems: z.optional(z.enum(["nested", "flat", "hidden"])),
    density: z.optional(z.enum(["comfortable", "compact"])),
    column_widths: z.optional(z.record(z.string(), z.number())),
    wip_limits: z.optional(z.record(z.string(), z.int().check(z.positive()))),
    swimlanes: z.optional(z.string()),
    /** The list's sub-grouping (2026-10-03): none, by project or by the client's milestones. */
    sub_group_by: z.optional(z.enum(["none", "project", "milestone_stage"])),
    /** All tasks' "Show client tasks" (2026-10-01): whether the client tasks no sprint holds are listed. */
    show_client_tasks: z.optional(z.boolean()),
    collapsed_columns: z.optional(z.array(z.string())),
    hidden_columns: z.optional(z.array(z.string())),
    collapsed_lanes: z.optional(z.array(z.string())),
    timeline_zoom: z.optional(z.enum(["day", "week", "month", "quarter"])),
    timeline_from: z.optional(z.string()),
    /** The calendar's view (2026-09-28): a month or a week. The day it shows rides in the URL only, so a fresh visit opens on today. */
    calendar_view: z.optional(z.enum(["month", "week"])),
    /** Whether the calendar's No date tray is open. */
    calendar_tray: z.optional(z.boolean()),
    /** The meetings calendar's view on a wide surface and on a phone (018 FR-018-282, 2026-09-29), each remembered per Team. */
    meeting_calendar_view: z.optional(z.enum(["day", "work_week", "week", "month"])),
    meeting_calendar_phone_view: z.optional(z.enum(["day", "agenda"])),
    /** Whether the meetings calendar shows the meetings the viewer declined ("Show declined meetings") (018 FR-018-303, 2026-09-30), remembered per Team. */
    meeting_show_declined: z.optional(z.boolean()),
    table_columns: z.optional(z.array(z.string())),
    table_pinned: z.optional(z.array(z.string())),
    table_totals: z.optional(z.array(z.string())),
    table_row_height: z.optional(z.enum(["short", "medium", "tall"])),
  }),
});

export const preferencesKey = defineKey({
  name: "preferences",
  version: 1,
  schema: z.object({
    theme: z.optional(z.enum(["light", "dark", "system"])),
    density: z.optional(z.enum(["comfortable", "compact"])),
    motion: z.optional(z.enum(["full", "reduced", "system"])),
    locale: z.optional(z.string()),
    shortcuts: z.optional(z.boolean()),
    /** Whether a newly created task opens in the peek; off by default (§ New item dialog). */
    autoOpenNewTask: z.optional(z.boolean()),
  }),
});

export const lastRouteKey = defineKey({ name: "last-route", version: 1, schema: z.string().check(z.startsWith("/")) });

/** Each rail mode's last route in one workspace, keyed by mode id, so a mode tile reopens where the person left that mode (FR-015-005). */
export const modeRouteKey = defineKey({ name: "mode-route", version: 1, schema: z.record(z.string(), z.string().check(z.startsWith("/"))) });


export const peekWidthKey = defineKey({ name: "peek-width", version: 1, schema: z.int().check(z.minimum(380), z.maximum(720)) });

/** How an item opens when the link names no presentation: the item dialog, the side peek or, since 2026-10-05, the full page, as the person last chose (015 FR-015-040 as amended). */
export const itemPresentationKey = defineKey({ name: "item-presentation", version: 1, schema: z.enum(["modal", "peek", "page"]) });

/** The frame an email opens in, the side peek, the dialog or, since 2026-10-07, its full page, as the person last chose, apart from an item's (022 FR-022-364 as amended 2026-10-05 and 2026-10-07). */
export const emailPresentationKey = defineKey({ name: "email-presentation", version: 1, schema: z.enum(["modal", "peek", "page"]) });

export const restoredAtKey = defineKey({ name: "restored-at", version: 1, schema: z.nullable(z.string()) });

export const lastWorkspaceKey = defineKey({ name: "last-workspace", version: 1, schema: z.string().check(z.minLength(1)) });

/** Each Team's address as this device last saw it, by the Team's id (001 FR-157, 2026-09-29): how a device that was not watching a Team when its address changed learns that it did, the next time `GET /me` is read, and moves what it keeps under the old address. */
export const teamAddressesKey = defineKey({ name: "team-addresses", version: 1, schema: z.record(z.string(), z.string()) });

/** The onboarding wizard's progress, so a reload resumes at the same step with the created
 * workspace already known (`contracts/screens.md` § Onboarding wizard). */
export const onboardingProgressKey = defineKey({
  name: "onboarding",
  version: 1,
  schema: z.object({
    step: z.int().check(z.minimum(1), z.maximum(4)),
    workspace: z.nullable(z.object({ slug: z.string().check(z.minLength(1)), name: z.string() })),
    project: z.nullable(z.object({ identifier: z.string().check(z.minLength(1)), name: z.string() })),
    invitesSent: z.int().check(z.minimum(0)),
  }),
});

/** The inbox filter the person last chose, scoped to the workspace, so the inbox reopens on it; the Unread default applies until they choose one (FR-015-160 as amended 2026-09-29). */
export const inboxFilterKey = defineKey({ name: "inbox-filter", version: 1, schema: z.enum(["all", "unread", "mentions", "assigned", "archived"]) });

/** The sidebar's width, resizable between the two component tokens (§ The shell). */
export const sidebarWidthKey = defineKey({ name: "sidebar-width", version: 1, schema: z.int().check(z.minimum(260), z.maximum(360)) });

/** Whether the sidebar is hidden, leaving the rail alone beside the sheet (§ The shell, 2026-09-17). */
export const sidebarCollapsedKey = defineKey({ name: "sidebar-collapsed", version: 1, schema: z.boolean() });

/** Which of a meeting page's foldable panels stand open, as the viewer last chose on any meeting, by panel (018 FR-018-315, 2026-10-02): the speaker strip, since the chapters' panel left the page on 2026-10-03 and an entry stored for it before then is dropped on read; a panel absent takes its own default. */
export const meetingPanelsOpenKey = defineKey({ name: "meeting-panels-open", version: 1, schema: z.object({ speakers: z.optional(z.boolean()) }) });

/** The project the last inline create on a cross-project list or board used, so the next composer opens on it (§ List layout, 2026-09-18). */
export const lastCreateProjectKey = defineKey({ name: "last-create-project", version: 1, schema: z.string().check(z.minLength(1)) });

/** Which of the sidebar's branching rows (Sprints, Projects) are open to their recent entries, by row id, scoped to the workspace (§ Sidebar § Home, 2026-09-17). */
export const sidebarBranchesKey = defineKey({ name: "sidebar-branches", version: 1, schema: z.record(z.string(), z.boolean()) });

/** How tall each open branch of the sidebar last drew its entries, in px by row id, scoped to the workspace, so a reload's placeholder holds that space and no row under the branch moves when the entries arrive (the owner's meeting of 2026-09-30). */
export const sidebarBranchHeightsKey = defineKey({ name: "sidebar-branch-heights", version: 1, schema: z.record(z.string(), z.int().check(z.minimum(0), z.maximum(4000))) });

/** Which client headings of the sidebar's Projects branch are collapsed, by heading id, scoped to the workspace (020 FR-020-173, beside the branches above). */
export const sidebarClientsKey = defineKey({ name: "sidebar-clients", version: 1, schema: z.record(z.string(), z.boolean()) });

/** The client list's columns Display adds to its default set, scoped to the workspace (015 `contracts/screens.md` § 020 Accounts and pipeline, the block of 2026-09-27, The client list). */
/** Version 2 (2026-10-03, 020 FR-020-280): the owner and the status columns join, both shown until a viewer hides them; a version 1 value reads as none, so the defaults show. */
/** Version 3 (2026-10-04, the owner's approval of the clients design audit): each status column is chosen on its own, To decide off by default; a version 2 value reads as none, so the defaults show. */
/** Version 4 (2026-10-04, the owner's decision removing whom a task is for): Owed to client went with it; a version 3 value keeps every column it chose but that one. */
/** Version 5 (2026-10-07, the owner's decision that the Team owns its clients): Owner went with the client's owner; a version 3 or 4 value keeps every column it chose but those gone. */
/** Version 6 (2026-10-15, 020 FR-020-300): Last interaction joins, shown until a viewer hides it; a version 3 to 5 value keeps every column it chose but those gone, and gains it. */
const ACCOUNT_LIST_COLUMNS = ["last_interaction", "type", "status", "open", "overdue", "last_contact", "next_meeting", "to_decide"] as const;
type AccountListColumn = (typeof ACCOUNT_LIST_COLUMNS)[number];
export const accountListColumnsKey = defineKey({
  name: "account-list-columns",
  version: 6,
  schema: z.array(z.enum(ACCOUNT_LIST_COLUMNS)),
  migrate: (from, data): AccountListColumn[] | null => {
    if (from < 3 || !Array.isArray(data)) return null;
    const kept = data.filter((column): column is AccountListColumn => (ACCOUNT_LIST_COLUMNS as readonly unknown[]).includes(column));
    return kept.includes("last_interaction") ? kept : ["last_interaction", ...kept];
  },
});

/** The sections the client hub last drew for one client, scoped to the workspace and the client, which the hub's loading skeleton draws (`contracts/design-system.md` § Empty, the paragraph of 2026-09-28). */
export const clientHubSectionsKey = defineKey({ name: "client-hub-sections", version: 1, schema: z.array(z.string()) });

/** Whether the reader hid Home's Set up card for the workspace (015 `contracts/screens.md` § Home, 2026-10-21): a per-browser convenience, scoped to the workspace. */
export const homeSetupHiddenKey = defineKey({ name: "home-setup-hidden", version: 1, schema: z.boolean() });

/** Which country headings of the Clients sidebar's list of clients are collapsed, by heading id (`country:PT`, or `area:002` for the clients located by an area alone), scoped to the workspace (015 `contracts/screens.md` § 020 Accounts and pipeline, the owner's decision of 2026-09-28 that clients are grouped by country). */
export const sidebarClientCountriesKey = defineKey({ name: "sidebar-client-countries", version: 1, schema: z.record(z.string(), z.boolean()) });

/** Which country headings of the Tickets mode's sidebar are collapsed, by the same heading ids, scoped to the workspace (028, `contracts/screens.md` § 028 Client portal, the Tickets mode as amended 2026-10-21): its own, since it lists only the clients that have tickets. */
export const sidebarTicketCountriesKey = defineKey({ name: "sidebar-ticket-countries", version: 1, schema: z.record(z.string(), z.boolean()) });

/** Which grouping rows of the client list are collapsed, by the grouping's own heading id (`country:PT`, `area:002`, `group:<id>`, `milestone:<key>`, `type:<id>`), scoped to the workspace (015 `contracts/screens.md` § 020 Accounts and pipeline, the owner's decision of 2026-09-28 that the list is grouped by country by default). */
export const clientListHeadingsKey = defineKey({ name: "client-list-headings", version: 1, schema: z.record(z.string(), z.boolean()) });

/** What the palette's recent items may be (2026-10-13): a task, a client or a meeting the person opened. */
export const RECENT_KINDS = ["task", "client", "meeting"] as const;

const recentItemsSchema = z.array(z.object({ route: z.string().check(z.startsWith("/")), label: z.string(), kind: z.enum(RECENT_KINDS) })).check(z.maxLength(20));

/**
 * The tasks, clients and meetings the person opened, newest first, each with its route and its label,
 * scoped to the Team and the person as `{ws}:{user id}` (§ Command palette, 2026-10-13). Version 1 held
 * whatever the palette itself opened, screens and projects among them, under the Team alone, and is
 * dropped rather than read. Version 2 labelled a task by its identifier and its title; since version 3
 * (2026-10-20, by the owner's decision that identifiers show only on the task page and peek) a task's
 * label is its title, so a version 2 list keeps its clients and meetings and drops its tasks, whose
 * labels cannot be rebuilt without reading them, until they are opened again.
 */
export const recentItemsKey = defineKey({
  name: "recent-items",
  version: 3,
  schema: recentItemsSchema,
  migrate: (from, data) => {
    if (from !== 2) return null;
    const parsed = recentItemsSchema.safeParse(data);
    return parsed.success ? parsed.data.filter((entry) => entry.kind !== "task") : null;
  },
});

/** The invite token carried through an authentication round trip (§ OAuth return). */
export const pendingInviteKey = defineKey({ name: "pending-invite", version: 1, schema: z.string().check(z.minLength(1)) });

/**
 * 019 `contracts/design-system.md` § Assistant panel: whether the panel is open and its width between
 * 360 and 560 px, remembered in this browser apart from the peek's width, so a reload opens the panel
 * as it was with nothing flashing (015 `contracts/screens.md` § 019 Assistant: its open state and width
 * are kept here and never in the address).
 */
export const assistantPanelKey = defineKey({ name: "assistant-panel", version: 1, schema: z.object({ open: z.boolean(), width: z.int().check(z.minimum(360), z.maximum(560)) }) });

/** The thread the panel holds in each Team, by the Team's slug, so the conversation survives a reload and a switch of Team opens that Team's own (2026-10-14). */
export const assistantThreadKey = defineKey({ name: "assistant-thread", version: 1, schema: z.record(z.string(), z.string().check(z.minLength(1))) });
